// Equation-review data layer (PRD 07 §9.4). TanStack Query hooks for the
// EquationsReviewPage. Saves + publishes via the routes in
// backend/api/routes/equations.py.

import { apiFetch } from "../../lib/apiClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Definition, ProposedEquations } from "../../data/types";


export interface SymbolReviewAction {
  action: "accepted" | "edited" | "rejected" | "pending";
  actor: string;
  at: string;
  edited_definition?: Definition | null;
  edited_role?: string | null;
  edited_units?: string | null;
}

export interface EquationReviewAction {
  action: "accepted" | "edited" | "rejected" | "pending";
  actor: string;
  at: string;
  edited_summary?: Definition | null;
  edited_interactions?: string | null;
  symbols: Record<string, SymbolReviewAction>;
}

export interface EquationReviewState {
  doc_id: string;
  extraction_equation_version: number;
  equations: Record<string, EquationReviewAction>;
  saved_at: string;
}

export interface EquationsReviewResponse {
  doc_id: string;
  proposed: ProposedEquations;
  review_state: EquationReviewState | null;
}

const equationsKey = (docId: string) =>
  ["equations-review", docId] as const;

export function useEquationsReview(docId: string) {
  return useQuery<EquationsReviewResponse, Error>({
    queryKey: equationsKey(docId),
    queryFn: async () => {
      const r = await apiFetch(
        `/docs/${encodeURIComponent(docId)}/equations`,
        { headers: { Accept: "application/json" } },
      );
      if (!r.ok) throw new Error(`equations fetch failed: ${r.status}`);
      return (await r.json()) as EquationsReviewResponse;
    },
    staleTime: 30_000,
  });
}

export function useSaveEquationsReview(docId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, EquationReviewState>({
    mutationFn: async (body) => {
      const r = await apiFetch(
        `/docs/${encodeURIComponent(docId)}/equations/review`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
      );
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`save failed (${r.status}): ${detail}`);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: equationsKey(docId) });
    },
  });
}

export function usePublishEquations(docId: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: async (actor) => {
      const r = await apiFetch(
        `/docs/${encodeURIComponent(docId)}/equations/publish`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ actor }),
        },
      );
      if (!r.ok) {
        const detail = await r.text();
        throw new Error(`publish failed (${r.status}): ${detail}`);
      }
      return (await r.json()) as unknown;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: equationsKey(docId) });
    },
  });
}
